import { useEffect, useMemo, useState } from "react";
import { Bell, BellRing, Eye, Trophy } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import type { ProductDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { rm, credits } from "../lib/format";
import { getSocket } from "../lib/socket";
import { EASE } from "../lib/motion";
import { sfx } from "../lib/sfx";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { ProductHeroImage, ProductImage, heroCaptionTextClass } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { Countdown } from "../components/Countdown";
import { Badge, Button, PageLoader, statusColor } from "../components/ui";
import {
  MyRaffleTickets,
  RaffleDrawCeremony,
  RafflePrizeModal,
  TicketWall,
  type DrawFx,
  type RafflePrizePreview,
} from "../components/raffle";
import { CountUp } from "../components/fx";
import { isGlobalEntryOpen, isLobbyItemWindowOpen, isLobbyStarting } from "../lib/raffleLobby";

interface RaffleItemDetail {
  id: string;
  sortOrder: number;
  winnerCount: number;
  maxTicketsPerUser?: number | null;
  priceForWinner: number;
  status: string;
  entryOpensAt: string | null;
  entryClosesAt: string | null;
  drawnAt?: string | null;
  ticketCount?: number;
  ticketNos?: number[];
  product: ProductDto;
  winners: { rank: number; ticketNo: number; name: string; orderId: string | null }[];
}

interface RaffleDetail {
  id: string;
  title: string;
  description: string | null;
  mode: "SINGLE" | "LOBBY";
  status: string;
  entryOpensAt: string;
  entryClosesAt: string;
  maxTicketsPerUser: number;
  entryCostCredits: number;
  priceForWinner: number;
  paymentDeadlineHours: number;
  drawDelaySeconds: number;
  interItemDelaySeconds: number;
  currentItemIndex: number;
  ticketCount: number;
  poolTicketNos?: number[];
  myTickets: { ticketNo: number; createdAt: string; raffleItemId: string | null }[];
  myWins: { ticketNo: number; orderId: string | null; rank: number; raffleItemId: string }[];
  winnerName: string | null;
  hasReminder: boolean;
  isWinner: boolean;
  orderId: string | null;
  paymentOrderId: string | null;
  paymentOrderPaid: boolean;
  serverSeedHash: string | null;
  serverSeed: string | null;
  clientSeed: string | null;
  drawProof: { winnerTicketNo?: number; roll?: number } | null;
  product: ProductDto | null;
  items: RaffleItemDetail[];
}

interface NextWindowHint {
  productName: string;
  opensAt: string;
}

const MIN_LOBBY_TICKET_WINDOW_SECONDS = 30;

function lobbyWindowSeconds(drawDelaySeconds: number) {
  return Math.max(drawDelaySeconds, MIN_LOBBY_TICKET_WINDOW_SECONDS);
}

function ticketsForItem(
  myTickets: RaffleDetail["myTickets"],
  itemId: string | null | undefined,
) {
  if (!itemId) return [];
  return myTickets.filter((ticket) => ticket.raffleItemId === itemId);
}

function effectiveMaxTickets(raffle: RaffleDetail, item: RaffleItemDetail | null | undefined) {
  if (item?.maxTicketsPerUser != null) return item.maxTicketsPerUser;
  return raffle.maxTicketsPerUser;
}

function TicketAllowanceLine({
  have,
  max,
  signedIn,
}: {
  have: number;
  max: number;
  signedIn: boolean;
}) {
  const left = Math.max(0, max - have);
  if (!signedIn) {
    return (
      <p className="text-center text-[10px] text-white/40">
        Up to {max} ticket{max !== 1 ? "s" : ""} per collector
      </p>
    );
  }
  if (have > max) {
    return (
      <p className="text-center text-[10px] text-white/40">
        You have <span className="font-medium text-white/55">{have}</span> ticket{have !== 1 ? "s" : ""}{" "}
        (limit is now {max} per prize)
      </p>
    );
  }
  if (left === 0) {
    return (
      <p className="text-center text-[10px] text-white/40">
        {max === 1
          ? "You already have your ticket for this prize"
          : `You have all ${max} tickets allowed for this prize`}
      </p>
    );
  }
  return (
    <p className="text-center text-[10px] text-white/40">
      {have > 0 ? (
        <>
          <span className="font-medium text-white/55">{have}</span> of {max} tickets ·{" "}
          <span className="font-medium text-white/55">{left}</span> more you can get
        </>
      ) : (
        <>
          You can get up to {max} ticket{max !== 1 ? "s" : ""}
        </>
      )}
    </p>
  );
}

export function RaffleRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [viewers, setViewers] = useState<number | null>(null);
  const [entering, setEntering] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [drawFx, setDrawFx] = useState<DrawFx | null>(null);
  const [nextWindow, setNextWindow] = useState<NextWindowHint | null>(null);
  const [prizePreview, setPrizePreview] = useState<RafflePrizePreview | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["raffle", id],
    queryFn: () => api<{ raffle: RaffleDetail }>(`/raffles/${id}`),
    refetchInterval: (query) => {
      const raffle = query.state.data?.raffle;
      if (!raffle) return false;
      if (raffle.status === "COMPLETED" || raffle.status === "CANCELLED") return false;
      return 10_000;
    },
  });

  useEffect(() => {
    setViewers(null);
    setDrawFx(null);
    setNextWindow(null);
    setPrizePreview(null);
    setEntering(false);
    setReminderBusy(false);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const socket = getSocket();
    const join = () => {
      socket.emit("raffle:join", id);
    };
    join();
    const pingPresence = () => {
      if (socket.connected) socket.emit("raffle:presence:ping", id);
    };
    const presencePing = globalThis.setInterval(pingPresence, 120_000);

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["raffle", id] });
    const onPresence = (p: { raffleId: string; count: number }) => {
      if (p.raffleId === id) setViewers(p.count);
    };
    const onRaffleRefresh = () => invalidate();
    const onDrawing = () => invalidate();
    const onDrawn = (p: {
      winnerName?: string;
      winnerTicketNo?: number;
      mode?: string;
      ticketNos?: number[];
    }) => {
      if (p.mode === "LOBBY") {
        // item-drawn already opened the ceremony; refetch after it closes
        return;
      }
      if (p.winnerName && p.winnerTicketNo != null) {
        setDrawFx({
          kind: "single",
          winnerName: p.winnerName,
          winnerTicketNo: p.winnerTicketNo,
          ticketNos: p.ticketNos,
        });
      }
      void invalidate();
    };
    const onItemDrawn = (p: {
      itemId: string;
      productName: string;
      ticketCount: number;
      ticketNos?: number[];
      winners: { name: string; ticketNo: number; orderId?: string }[];
      noEntries?: boolean;
    }) => {
      setDrawFx({
        kind: "lobby",
        itemId: p.itemId,
        productName: p.productName,
        ticketCount: p.ticketCount,
        ticketNos: p.ticketNos,
        winners: p.winners,
        noEntries: p.noEntries ?? (p.ticketCount === 0 && p.winners.length === 0),
      });
      void invalidate();
    };
    const onNextItem = (p: {
      productName?: string;
      startsInSeconds: number;
      opensAt?: string;
      opensTicketWindow?: boolean;
    }) => {
      if (!p.opensTicketWindow) return;
      setNextWindow({
        productName: p.productName ?? "Next prize",
        opensAt: p.opensAt ?? new Date(Date.now() + p.startsInSeconds * 1000).toISOString(),
      });
      void invalidate();
    };
    const onItemWindowOpen = () => {
      setNextWindow(null);
      void invalidate();
    };
    socket.on("raffle:presence", onPresence);
    socket.on("raffle:drawing", onDrawing);
    socket.on("raffle:item-drawing", onDrawing);
    socket.on("raffle:lobby-drawing", onDrawing);
    socket.on("raffle:drawn", onDrawn);
    socket.on("raffle:item-drawn", onItemDrawn);
    socket.on("raffle:next-item", onNextItem);
    socket.on("raffle:item-window-open", onItemWindowOpen);
    socket.on("raffle:redraw", onRaffleRefresh);
    socket.on("raffle:opened", onRaffleRefresh);
    socket.on("raffle:completed", onRaffleRefresh);
    socket.on("raffle:cancelled", onRaffleRefresh);
    socket.on("raffle:forfeit", onRaffleRefresh);
    socket.on("raffle:ticket:withdrawn", onRaffleRefresh);
    socket.on("connect", join);
    return () => {
      globalThis.clearInterval(presencePing);
      socket.emit("raffle:leave", id);
      socket.off("connect", join);
      socket.off("raffle:presence", onPresence);
      socket.off("raffle:drawing", onDrawing);
      socket.off("raffle:item-drawing", onDrawing);
      socket.off("raffle:lobby-drawing", onDrawing);
      socket.off("raffle:drawn", onDrawn);
      socket.off("raffle:item-drawn", onItemDrawn);
      socket.off("raffle:next-item", onNextItem);
      socket.off("raffle:item-window-open", onItemWindowOpen);
      socket.off("raffle:redraw", onRaffleRefresh);
      socket.off("raffle:opened", onRaffleRefresh);
      socket.off("raffle:completed", onRaffleRefresh);
      socket.off("raffle:cancelled", onRaffleRefresh);
      socket.off("raffle:forfeit", onRaffleRefresh);
      socket.off("raffle:ticket:withdrawn", onRaffleRefresh);
    };
  }, [id, queryClient]);

  const activeItem = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY") return null;
    return (
      data.raffle.items.find((i) => i.status === "ENTRY_OPEN") ??
      data.raffle.items.find((i) => i.status === "DRAWING") ??
      data.raffle.items[data.raffle.currentItemIndex] ??
      null
    );
  }, [data?.raffle]);

  const lobbyTicketWindowOpen = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY" || data.raffle.status !== "DRAWING") return false;
    return data.raffle.items.some((item) => isLobbyItemWindowOpen(item));
  }, [data?.raffle]);

  const openLobbyItem = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY" || data.raffle.status !== "DRAWING") return null;
    return data.raffle.items.find((item) => isLobbyItemWindowOpen(item)) ?? null;
  }, [data?.raffle]);

  const drawingItem = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY") return null;
    return data.raffle.items.find((i) => i.status === "DRAWING") ?? null;
  }, [data?.raffle]);

  const poolItem = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY") return null;
    if (openLobbyItem) return openLobbyItem;
    if (drawingItem) return drawingItem;
    if (data.raffle.status === "OPEN" && (isGlobalEntryOpen(data.raffle) || isLobbyStarting(data.raffle))) {
      return data.raffle.items[0] ?? null;
    }
    return null;
  }, [data?.raffle, openLobbyItem, drawingItem]);

  const lobbyStarting = useMemo(
    () => (data?.raffle ? isLobbyStarting(data.raffle) : false),
    [data?.raffle],
  );

  useEffect(() => {
    if (!id || !lobbyStarting) return;
    const interval = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ["raffle", id] });
    }, 4000);
    return () => clearInterval(interval);
  }, [id, lobbyStarting, queryClient]);

  const windowTickets = useMemo(() => {
    if (!data?.raffle || !poolItem) return [];
    return ticketsForItem(data.raffle.myTickets, poolItem.id);
  }, [data?.raffle, poolItem]);

  useEffect(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY" || data.raffle.status !== "DRAWING") return;
    if (openLobbyItem) {
      setNextWindow(null);
      return;
    }
    const nextPending = data.raffle.items.find((item) => item.status === "PENDING");
    const lastDone = [...data.raffle.items].reverse().find((item) => item.status === "DONE" && item.drawnAt);
    if (!nextPending || !lastDone?.drawnAt) return;
    const opensAt = new Date(new Date(lastDone.drawnAt).getTime() + data.raffle.interItemDelaySeconds * 1000);
    if (opensAt.getTime() <= Date.now()) return;
    setNextWindow({
      productName: nextPending.product.name,
      opensAt: opensAt.toISOString(),
    });
  }, [data?.raffle, openLobbyItem]);

  const upcomingLobbyItem = useMemo(() => {
    if (!data?.raffle || data.raffle.mode !== "LOBBY" || !nextWindow) return null;
    return (
      data.raffle.items.find((item) => item.status === "PENDING") ??
      data.raffle.items.find((item) => item.product.name === nextWindow.productName) ??
      null
    );
  }, [data?.raffle, nextWindow]);

  const displayProduct = useMemo(() => {
    if (!data?.raffle) return null;
    if (data.raffle.mode === "LOBBY") {
      return activeItem?.product ?? data.raffle.items[0]?.product ?? data.raffle.product;
    }
    return data.raffle.product;
  }, [data?.raffle, activeItem]);

  if (isLoading) return <PageLoader />;

  if (isError || !data) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="font-display text-sm text-white/70">This raffle is not available.</p>
        <Link to="/raffles" className="mt-3 inline-block text-xs font-semibold text-brand-pink">
          Back to raffles
        </Link>
      </div>
    );
  }

  if (!displayProduct) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="font-display text-sm text-white/70">This raffle has no prize configured.</p>
        <Link to="/raffles" className="mt-3 inline-block text-xs font-semibold text-brand-pink">
          Back to raffles
        </Link>
      </div>
    );
  }

  const raffle = data.raffle;
  const globalEntryOpen = isGlobalEntryOpen(raffle);
  const lobbyGlobalOrStarting =
    raffle.mode === "LOBBY" && (globalEntryOpen || lobbyStarting);
  const firstLobbyItem = raffle.mode === "LOBBY" ? (raffle.items[0] ?? null) : null;
  const singleStartingCountdown =
    raffle.mode === "SINGLE" &&
    raffle.status === "DRAWING" &&
    new Date(raffle.entryClosesAt).getTime() > Date.now();
  const lobbyEntryItem =
    raffle.mode === "LOBBY"
      ? globalEntryOpen || lobbyStarting
        ? firstLobbyItem
        : openLobbyItem
      : null;
  const entryTickets =
    raffle.mode === "LOBBY"
      ? firstLobbyItem && lobbyGlobalOrStarting
        ? ticketsForItem(raffle.myTickets, firstLobbyItem.id)
        : lobbyEntryItem
          ? ticketsForItem(raffle.myTickets, lobbyEntryItem.id)
          : []
      : raffle.myTickets;
  const showPrizeOneBanner = lobbyGlobalOrStarting && entryTickets.length > 0;
  const betweenWindowTickets = upcomingLobbyItem
    ? ticketsForItem(raffle.myTickets, upcomingLobbyItem.id)
    : [];
  const activeTicketMax = effectiveMaxTickets(raffle, poolItem ?? lobbyEntryItem);
  const betweenWindowUnderLimit = betweenWindowTickets.length < effectiveMaxTickets(raffle, upcomingLobbyItem);
  const underTicketLimit = entryTickets.length < activeTicketMax;
  const canEnter =
    underTicketLimit &&
    (globalEntryOpen || singleStartingCountdown || (raffle.mode === "LOBBY" && lobbyTicketWindowOpen));
  const removableTickets =
    openLobbyItem && lobbyTicketWindowOpen
      ? windowTickets
      : globalEntryOpen || singleStartingCountdown
        ? entryTickets
        : [];
  const displayTicketCount =
    raffle.mode === "LOBBY" ? (poolItem?.ticketCount ?? 0) : raffle.ticketCount;
  const displayTicketNos =
    raffle.mode === "LOBBY" ? (poolItem?.ticketNos ?? []) : (raffle.poolTicketNos ?? []);
  const ceremonyMyTicketNos =
    drawFx?.kind === "lobby"
      ? ticketsForItem(raffle.myTickets, drawFx.itemId).map((t) => t.ticketNo)
      : entryTickets.map((t) => t.ticketNo);

  const ticketPoolKey = poolItem?.id ?? "idle";

  async function enter() {
    if (!user) {
      navigate("/login");
      return;
    }
    setEntering(true);
    try {
      const res = await api<{ ticketNo: number; ticketNos?: number[] }>(`/raffles/${raffle.id}/enter`, { body: {} });
      sfx.reveal(1);
      const issued = res.ticketNos?.length ? res.ticketNos : [res.ticketNo];
      toast(
        "success",
        issued.length > 1
          ? `You're in! Tickets ${issued.map((n) => `#${n}`).join(", ")}`
          : `You're in! Ticket #${res.ticketNo}`,
      );
      await queryClient.invalidateQueries({ queryKey: ["raffle", id] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't enter");
    } finally {
      setEntering(false);
    }
  }

  async function toggleReminder() {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!data?.raffle) return;
    const next = !data.raffle.hasReminder;
    setReminderBusy(true);
    try {
      await api(`/raffles/${data.raffle.id}/reminder`, { method: "PUT", body: { enabled: next } });
      toast("success", next ? "Reminder set — we'll notify you when entries open" : "Reminder removed");
      await queryClient.invalidateQueries({ queryKey: ["raffle", id] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update reminder");
    } finally {
      setReminderBusy(false);
    }
  }

  const winnerPrice =
    raffle.mode === "LOBBY" && activeItem ? activeItem.priceForWinner : raffle.priceForWinner;

  const ceremonyPrize =
    drawFx?.kind === "lobby"
      ? (raffle.items.find((i) => i.id === drawFx.itemId)?.product ?? displayProduct)
      : displayProduct;

  const ceremonyIsWinner = drawFx
    ? drawFx.kind === "single"
      ? entryTickets.some((t) => t.ticketNo === drawFx.winnerTicketNo)
      : drawFx.winners.some((w) =>
          ticketsForItem(raffle.myTickets, drawFx.itemId).some((t) => t.ticketNo === w.ticketNo),
        )
    : false;

  const ceremonyPaymentOrderId =
    drawFx?.kind === "lobby" && ceremonyIsWinner
      ? (() => {
          const myTicketNos = ticketsForItem(raffle.myTickets, drawFx.itemId).map((t) => t.ticketNo);
          return drawFx.winners.find((w) => myTicketNos.includes(w.ticketNo))?.orderId ?? null;
        })()
      : null;
  const paymentOrderId = ceremonyPaymentOrderId ?? raffle.paymentOrderId ?? raffle.orderId;

  function openPrizePreview(preview: RafflePrizePreview) {
    setPrizePreview(preview);
  }

  function heroPrizePreview(): RafflePrizePreview {
    const lobbyItem =
      raffle.mode === "LOBBY" && activeItem ? activeItem : raffle.mode === "LOBBY" ? raffle.items[0] : null;
    return {
      product: displayProduct!,
      priceForWinner: winnerPrice,
      prizeIndex:
        lobbyItem && raffle.mode === "LOBBY"
          ? raffle.items.findIndex((item) => item.id === lobbyItem.id) + 1
          : undefined,
      winnerCount: lobbyItem?.winnerCount,
      status: lobbyItem?.status,
    };
  }

  return (
    <div className="min-w-0 max-w-full space-y-5 px-4 pt-4 lg:px-6">
      <RaffleDrawCeremony
        fx={drawFx}
        ticketNos={displayTicketNos}
        myTicketNos={ceremonyMyTicketNos}
        prizeName={ceremonyPrize.name}
        prizeImage={ceremonyPrize.images[0] ?? null}
        prizeImageBackgroundColor={ceremonyPrize.imageBackgroundColor}
        isWinner={ceremonyIsWinner}
        paymentHref={paymentOrderId ? `/checkout/order/${paymentOrderId}` : null}
        paymentPending={ceremonyIsWinner && !raffle.paymentOrderPaid && !!paymentOrderId}
        onClose={() => {
          setDrawFx(null);
          void queryClient.invalidateQueries({ queryKey: ["raffle", id] });
        }}
      />
      <RafflePrizeModal prize={prizePreview} onClose={() => setPrizePreview(null)} />

      {/* Grid at lg: col1 = hero + prize queue/description/ticket wall (rows 1-2);
          col2 = buy action (sticky, row1) + how it works (row2).
          Below lg it's a single column and the JSX order below is the visual
          order, so the buy action sits right after the hero, before the queue. */}
      <div className="grid min-w-0 max-w-full gap-5 lg:grid-cols-[minmax(0,1fr)_400px] lg:items-start lg:gap-8">
        {/* ---- hero ---- */}
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: EASE.outExpo }}
          onClick={() => openPrizePreview(heroPrizePreview())}
          className="relative w-full min-w-0 max-w-full cursor-pointer overflow-hidden rounded-3xl border border-white/5 text-left transition-transform active:scale-[0.99] lg:col-start-1 lg:row-start-1 lg:hover:border-brand-pink/30"
        >
          <ProductHeroImage src={displayProduct.images[0] ?? null} overlay="full" />
          <div className="absolute left-3 top-3 z-30 flex flex-wrap gap-2">
            <Badge color={statusColor(raffle.status)}>{raffle.status}</Badge>
            {raffle.mode === "LOBBY" && <Badge color="pink">Lobby</Badge>}
            {viewers != null && (
              <Badge color="gray">
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-ring" />
                  <Eye size={12} /> {viewers} watching
                </span>
              </Badge>
            )}
          </div>
          <div className={`absolute bottom-0 left-0 right-0 z-30 p-4 lg:p-6 ${heroCaptionTextClass}`}>
            <h1 className="font-display text-lg font-bold leading-tight lg:text-3xl">{raffle.title}</h1>
            {raffle.mode === "LOBBY" && activeItem && (
              <p className="mt-0.5 text-[11px] text-white/80">
                {activeItem.status === "ENTRY_OPEN"
                  ? `Tickets open for: ${activeItem.product.name}`
                  : activeItem.status === "DRAWING"
                    ? `Drawing: ${activeItem.product.name}`
                    : `Up next: ${activeItem.product.name}`}{" "}
                · {activeItem.winnerCount} winner
                {activeItem.winnerCount !== 1 ? "s" : ""}
              </p>
            )}
            <p className="mt-1 text-xs text-white/90">
              Winner buys for <span className="font-semibold text-brand-pink">{rm(winnerPrice)}</span>{" "}
              <span className="text-white/65">(market {rm(displayProduct.price)})</span>
            </p>
            <p className="mt-2 text-[10px] font-medium text-white/50">Tap for prize details</p>
          </div>
        </motion.button>

        {/* ---- buy action / ticket status ---- */}
        <div className="glass-panel min-w-0 max-w-full rounded-2xl p-4 lg:col-start-2 lg:row-start-1 lg:sticky lg:top-20">
          {raffle.status === "SCHEDULED" && (
            <div className="space-y-3 text-center">
              <p className="text-xs text-white/60">Entries open in</p>
              <div className="flex justify-center">
                <Countdown target={raffle.entryOpensAt} onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })} />
              </div>
              <p className="text-[11px] text-white/45">You can&apos;t enter until the lobby opens. Set a reminder and we&apos;ll notify you.</p>
              {user ? (
                <Button
                  variant={raffle.hasReminder ? "outline" : "primary"}
                  className="w-full"
                  disabled={reminderBusy}
                  onClick={() => void toggleReminder()}
                >
                  {raffle.hasReminder ? (
                    <>
                      <BellRing size={16} className="mr-1.5 inline" />
                      Reminder set
                    </>
                  ) : (
                    <>
                      <Bell size={16} className="mr-1.5 inline" />
                      Remind me
                    </>
                  )}
                </Button>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    Sign in to set a reminder
                  </Button>
                </Link>
              )}
            </div>
          )}

          {raffle.status === "OPEN" && globalEntryOpen && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-white/50">Draw in</p>
                  <Countdown target={raffle.entryClosesAt} onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })} />
                  {raffle.mode === "LOBBY" && (
                    <p className="mt-1 text-[10px] text-white/40">
                      Enter now for prize #1, or grab a ticket when each prize&apos;s window opens
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-bold text-brand-pink lg:text-2xl">
                    <CountUp key={ticketPoolKey} value={displayTicketCount} />
                  </p>
                  <p className="text-[10px] text-white/50">tickets in</p>
                </div>
              </div>

              {showPrizeOneBanner && (
                <div className="rounded-xl bg-emerald-500/10 px-3 py-2 text-center text-xs text-emerald-300">
                  You&apos;re in for prize #1 with ticket{entryTickets.length > 1 ? "s" : ""}{" "}
                  {entryTickets.map((t) => `#${t.ticketNo}`).join(", ")}
                </div>
              )}

              <TicketAllowanceLine
                have={entryTickets.length}
                max={activeTicketMax}
                signedIn={Boolean(user)}
              />

              {canEnter && (
                <Button className="w-full pulse-ring" disabled={entering} onClick={enter}>
                  {entering
                    ? "Entering…"
                    : raffle.entryCostCredits > 0
                      ? `Get a ticket · ${credits(raffle.entryCostCredits)}`
                      : "Get a free ticket"}
                </Button>
              )}
            </div>
          )}

          {lobbyStarting && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="animate-pulse font-display text-sm font-bold text-brand-pink">
                    Lobby draws starting…
                  </p>
                  <p className="mt-1 text-[10px] text-white/40">
                    Main entries closed · per-prize ticket windows open shortly
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-bold text-brand-pink lg:text-2xl">
                    <CountUp key={ticketPoolKey} value={displayTicketCount} />
                  </p>
                  <p className="text-[10px] text-white/50">tickets in for prize #1</p>
                </div>
              </div>

              {showPrizeOneBanner && (
                <div className="rounded-xl bg-emerald-500/10 px-3 py-2 text-center text-xs text-emerald-300">
                  You&apos;re in for prize #1 with ticket{entryTickets.length > 1 ? "s" : ""}{" "}
                  {entryTickets.map((t) => `#${t.ticketNo}`).join(", ")}
                </div>
              )}

              <TicketAllowanceLine
                have={entryTickets.length}
                max={activeTicketMax}
                signedIn={Boolean(user)}
              />
            </div>
          )}

          {raffle.status === "OPEN" && !globalEntryOpen && !lobbyStarting && (
            <div className="space-y-3 py-2 text-center">
              <p className="text-xs text-white/50">Entries are not open yet.</p>
              <Countdown target={raffle.entryOpensAt} onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })} />
            </div>
          )}

          {raffle.status === "DRAWING" && (
            <div className="space-y-3">
              {singleStartingCountdown ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-white/50">Draw starting in</p>
                      <Countdown
                        target={raffle.entryClosesAt}
                        onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })}
                      />
                      <p className="mt-1 text-[10px] text-white/40">Last chance to grab a ticket before the draw</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-bold text-brand-pink lg:text-2xl">
                        <CountUp key={ticketPoolKey} value={displayTicketCount} />
                      </p>
                      <p className="text-[10px] text-white/50">tickets in</p>
                    </div>
                  </div>
                  {entryTickets.length > 0 && (
                    <div className="rounded-xl bg-emerald-500/10 px-3 py-2 text-center text-xs text-emerald-300">
                      You&apos;re in with ticket{entryTickets.length > 1 ? "s" : ""}{" "}
                      {entryTickets.map((t) => `#${t.ticketNo}`).join(", ")}
                    </div>
                  )}
                  <TicketAllowanceLine
                    have={entryTickets.length}
                    max={activeTicketMax}
                    signedIn={Boolean(user)}
                  />
                  {canEnter && (
                    <Button className="w-full pulse-ring" disabled={entering} onClick={enter}>
                      {entering
                        ? "Entering…"
                        : raffle.entryCostCredits > 0
                          ? `Get a ticket · ${credits(raffle.entryCostCredits)}`
                          : "Get a free ticket"}
                    </Button>
                  )}
                </>
              ) : openLobbyItem?.entryClosesAt ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-white/50">Tickets close for {openLobbyItem.product.name}</p>
                      <Countdown
                        target={openLobbyItem.entryClosesAt}
                        onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })}
                      />
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-bold text-brand-pink lg:text-2xl">
                        <CountUp key={ticketPoolKey} value={displayTicketCount} />
                      </p>
                      <p className="text-[10px] text-white/50">tickets in</p>
                    </div>
                  </div>
                  {windowTickets.length > 0 ? (
                    <div className="rounded-xl bg-emerald-500/10 px-3 py-2 text-center text-xs text-emerald-300">
                      You&apos;re in for this prize with ticket
                      {windowTickets.length > 1 ? "s" : ""}{" "}
                      {windowTickets.map((t) => `#${t.ticketNo}`).join(", ")}
                    </div>
                  ) : null}
                  <TicketAllowanceLine
                    have={windowTickets.length}
                    max={effectiveMaxTickets(raffle, openLobbyItem)}
                    signedIn={Boolean(user)}
                  />
                  {canEnter && (
                    <Button className="w-full pulse-ring" disabled={entering} onClick={enter}>
                      {entering
                        ? "Entering…"
                        : raffle.entryCostCredits > 0
                          ? `Get a ticket · ${credits(raffle.entryCostCredits)}`
                          : "Get a free ticket"}
                    </Button>
                  )}
                </>
              ) : (
                <div className="space-y-3 py-2 text-center">
                  {nextWindow ? (
                    <>
                      <p className="text-xs text-white/50">Next ticket window: {nextWindow.productName}</p>
                      <div className="flex justify-center">
                        <Countdown
                          target={nextWindow.opensAt}
                          onDone={() => queryClient.invalidateQueries({ queryKey: ["raffle", id] })}
                        />
                      </div>
                      <p className="text-[10px] text-white/40">
                        {betweenWindowUnderLimit
                          ? `Grab a ticket for this prize when the window opens (up to ${effectiveMaxTickets(raffle, upcomingLobbyItem)} per collector)`
                          : effectiveMaxTickets(raffle, upcomingLobbyItem) === 1
                            ? "You already have a ticket for this prize"
                            : `You already have the maximum ${effectiveMaxTickets(raffle, upcomingLobbyItem)} tickets for this prize`}
                      </p>
                      <TicketAllowanceLine
                        have={betweenWindowTickets.length}
                        max={effectiveMaxTickets(raffle, upcomingLobbyItem)}
                        signedIn={Boolean(user)}
                      />
                    </>
                  ) : (
                    <>
                      <p className="animate-pulse font-display text-sm font-bold text-brand-pink">
                        {raffle.mode === "LOBBY" ? "Drawing prizes…" : "Drawing the winner…"}
                      </p>
                      <p className="text-xs text-white/50">Waiting for the next ticket window…</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {["DRAWN", "COMPLETED"].includes(raffle.status) && (
            <div className="space-y-3 text-center">
              <Trophy size={40} strokeWidth={1.25} className="mx-auto text-amber-300" />
              {raffle.mode === "LOBBY" ? (
                <>
                  <p className="font-display text-sm font-bold">Lobby complete</p>
                  {raffle.myWins.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-emerald-300">You won {raffle.myWins.length} prize(s)!</p>
                      {raffle.paymentOrderPaid ? (
                        <p className="text-xs text-white/50">Payment complete — thanks!</p>
                      ) : raffle.paymentOrderId ? (
                        <Link to={`/checkout/order/${raffle.paymentOrderId}`}>
                          <Button className="w-full">Proceed to payment</Button>
                        </Link>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-xs text-white/50">See winners on each prize below.</p>
                  )}
                </>
              ) : (
                <>
                  <p className="font-display text-sm font-bold">
                    {raffle.isWinner ? "You won!" : raffle.winnerName ? `${raffle.winnerName} won` : "Drawn"}
                  </p>
                  {raffle.drawProof?.winnerTicketNo != null && (
                    <p className="text-[11px] text-white/50">Winning ticket #{raffle.drawProof.winnerTicketNo}</p>
                  )}
                  {raffle.isWinner && !raffle.paymentOrderPaid && raffle.paymentOrderId && raffle.status === "DRAWN" && (
                    <Link to={`/checkout/order/${raffle.paymentOrderId}`}>
                      <Button className="w-full">
                        Proceed to payment · {rm(raffle.priceForWinner)} within {raffle.paymentDeadlineHours}h
                      </Button>
                    </Link>
                  )}
                  {raffle.isWinner && raffle.paymentOrderPaid && (
                    <p className="text-xs text-white/50">Payment complete — thanks!</p>
                  )}
                </>
              )}
            </div>
          )}

          {raffle.status === "CANCELLED" && (
            <p className="py-2 text-center text-xs text-white/50">This raffle was cancelled.</p>
          )}

          {removableTickets.length > 0 && user && (
            <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
              <p className="text-center text-[10px] text-white/45">Remove a ticket before the draw</p>
              <MyRaffleTickets
                raffleId={raffle.id}
                tickets={removableTickets}
                onRemoved={() => void queryClient.invalidateQueries({ queryKey: ["raffle", id] })}
              />
            </div>
          )}
        </div>

        {/* ---- prize queue / description / ticket wall ---- */}
        <div className="min-w-0 max-w-full space-y-5 lg:col-start-1 lg:row-start-2">
          {raffle.mode === "LOBBY" && raffle.items.length > 0 && (
            <div className="min-w-0 rounded-2xl border border-white/5 bg-surface p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">Prize queue</p>
              <div className="scroll-row scroll-row-visible pb-1">
                {raffle.items.map((item, idx) => {
                  const active =
                    item.status === "ENTRY_OPEN" || item.status === "DRAWING" || raffle.currentItemIndex === idx;
                  const done = item.status === "DONE";
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() =>
                        openPrizePreview({
                          product: item.product,
                          priceForWinner: item.priceForWinner,
                          prizeIndex: idx + 1,
                          winnerCount: item.winnerCount,
                          status: item.status,
                        })
                      }
                      className={`w-32 shrink-0 rounded-xl border p-2 text-left transition-colors active:scale-[0.98] ${
                        active
                          ? "border-brand-pink/50 bg-brand-pink/10"
                          : done
                            ? "border-white/5 bg-white/[0.02] opacity-60"
                            : "border-white/5 bg-white/[0.03]"
                      }`}
                    >
                      <div className="relative">
                        <ProductImage
                          src={item.product.images[0] ?? null}
                          alt=""
                          backgroundColor={item.product.imageBackgroundColor}
                          className="aspect-[5/7] w-full rounded-lg"
                        />
                        <span className="absolute left-1 top-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[8px] font-bold text-white/80">
                          #{idx + 1}
                        </span>
                      </div>
                      <ListingTitle className="mt-1.5 text-[10px] leading-[14px] min-h-[14px]">{item.product.name}</ListingTitle>
                      <div className="mt-1 flex items-center justify-between text-[9px]">
                        <span className="text-white/45">{item.winnerCount}×</span>
                        <span className="font-semibold text-brand-pink">{rm(item.priceForWinner)}</span>
                      </div>
                      <p
                        className={`mt-1 text-center text-[8px] font-bold uppercase tracking-wide ${
                          active ? "text-brand-pink" : done ? "text-white/30" : "text-white/50"
                        }`}
                      >
                        {item.status.replace(/_/g, " ")}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {raffle.description && <p className="text-xs leading-relaxed text-white/60">{raffle.description}</p>}

          {/* the live pool of tickets */}
          {["OPEN", "DRAWING"].includes(raffle.status) && displayTicketCount > 0 && (
            <div className="rounded-2xl border border-white/5 bg-surface/60 p-4">
              <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest text-white/40">
                Tickets in the drum
              </p>
              <TicketWall
                key={ticketPoolKey}
                ticketNos={displayTicketNos}
                myTicketNos={windowTickets.map((t) => t.ticketNo)}
                shuffling={raffle.status === "DRAWING" && Boolean(poolItem)}
              />
            </div>
          )}
        </div>

        {/* ---- how it works ---- */}
        <div className="space-y-4 lg:col-start-2 lg:row-start-2">
          <div className="rounded-2xl border border-white/5 bg-surface/60 p-3.5 text-[11px] leading-relaxed text-white/50">
            <p className="mb-1 font-semibold text-white/70">How it works</p>
            {raffle.mode === "LOBBY" ? (
              <>
                Each prize has its own ticket pool — grab a ticket during the main countdown for prize #1, then again
                when each prize&apos;s window opens ({lobbyWindowSeconds(raffle.drawDelaySeconds)}s minimum). Up to{" "}
                {raffle.maxTicketsPerUser} ticket{raffle.maxTicketsPerUser !== 1 ? "s" : ""} per prize. Winners pay
                within {raffle.paymentDeadlineHours} hours to claim.
              </>
            ) : (
              <>
                Max {raffle.maxTicketsPerUser} ticket(s) per collector. When the timer ends the winner is drawn
                automatically — pay within {raffle.paymentDeadlineHours} hours or it may be re-drawn.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
